import { notification } from "ant-design-vue";
import { AxiosError } from "axios";
import { LLMRole } from "../models";
import { useUserStore } from "../store/modules/user";
import { getI18n } from "../i18n";
import { AIStandardEndpoint, ServerName } from "../utils/constants/serverName";
import axios from "../utils/http/axios";
import { showNotification } from "@/service/notification";

export interface ServerStatus {
  id: LLMServer;
  label: string;
  color: string;
  name: string;
}
export interface RAGIndexingSettings {
  chunkSize: number;
  chunkOverlap: number;
}
export interface LLMFileMetadata {
  status: "success" | "error";
  filename: string;
  id: string;
  size: number;
  file_format: string;
}
export interface LLMMessageParams {
  content: string;
  case_id?: string;
  role: LLMRole;
  topic?: string;
  temperature?: number;
  system_prompt?: string;
  model?: string;
  index_id?: string;
  server?: string;
  retrieval_prompt?: string;
  timestamp?: number;
  metadata?: LLMFileMetadata | Record<string, any> | null;
  thoughts?: string;
  artifact_type?: string; // mapped from artifact_type on the wire
}
export type LLMServer = string;

export interface InspectionChunkData {
  owner: string;
  tenant_id: number;
  date_created: number;
  document_id: string;
  chunk_id: string;
  text: string;
}

export interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  response?: string;
  url?: string;
  preview?: string;
  originFileObj: File;
  file: string | Blob;
}
export interface AddPromptDTO {
  name: string;
  prompt: string;
}
export interface LLMPrompt {
  name: string;
  prompt: string;
  id: string;
}
// Describes a single image-generation parameter a model exposes. Mirrors the
// orchestrator's ImageParam (models.yaml `image_params`): the source of truth
// for rendering controls and validating/defaulting values.
export interface ImageParam {
  name: string;
  type: "string" | "integer" | "float" | "enum" | "image";
  required?: boolean;
  description?: string;
  default?: string | number | null;
  min?: number;
  max?: number;
  options?: string[];
}

export interface LLMModel {
  value: string;
  label: string;
  serverId: string;
  disabled?: boolean;
  max_context_window?: number;
  provider: string;
  // Backend-driven list segmentation ("recommended", ...); missing -> fallback group.
  display_group?: string;
  capabilities: string[];
  order: number;
  thinking_options?: {
    type: "boolean" | "string";
    options?: string[];
    default?: string | boolean;
    disabled?: string[];
  };
  // Present only for models with the "image_generation" capability.
  image_params?: ImageParam[];
}
export interface RAGResponse {
  sources: RAGSource[];
  response: string[];
}
export interface RAGSource {
  id: string;
  name: string;
  text: string;
  score: number;
  documentId: string;
  matchType?: string; // hybrid retrieval origin: "semantic" | "keyword" | "both"
}

export interface WebSource {
  name: string;
  url: string;
  text: string;
  original_text?: string;
  score?: number;
}

// Helper types for external sources (for overview purposes)
export type ExternalSource = WebSource;
export type ExternalSourceType = "web";

// DTO for external sources
export type ExternalSourceDTO = {
  type: "web";
  data: WebSource;
}; // Extendable for other source types in the future

export const getServerNameFull = (id: string) => {
  switch (id) {
    case "default":
    case "hetzner":
    case "llm-orchestrator":
      return "Orchestrator";
    case "premium":
      return "Server 1";
    case "premium2":
      return "Server 2";
    case "premium3":
      return "Server 3";
    case "premium4":
      return "Server 4";
    case "":
      return "Server";
    default:
      return id;
  }
};

export const getLLMEndpoint = (server: string) => {
  return AIStandardEndpoint;
};

export const addURLToIndex = async (index_id: string, model: string, type: "case" | "playbook", url: string, chunkSize: number, chunkOverlap: number, playbook_id = "") => {
  const endpoint = `${AIStandardEndpoint}/v2/index`;
  const params: { [key: string]: any } = {};
  params["model"] = model;
  params["index_id"] = index_id;
  if (playbook_id.length > 0) {
    params["playbook_id"] = playbook_id;
  }

  params["chunk_size"] = chunkSize;
  params["chunk_overlap"] = chunkOverlap;
  params["index_type"] = type;
  params["url"] = url;

  try {
    const response = await axios.post(endpoint, params);
    return Promise.resolve();
  } catch (error) {
    console.warn(error);
    console.warn(`Could not communicate with LLM`);
    return Promise.resolve({
      response: ["Die Anfrage kann zurzeit nicht bearbeitet werden. Bitte versuchen Sie es später erneut."],
      sources: [],
    });
  }
};

export const getAvailableModelsList = async () => {
  let models: LLMModel[] = [];

  const store = useUserStore();

  const endpoint = `${AIStandardEndpoint}/v1/models/detailed`;
  try {
    const response = await axios.get<{
      models: {
        name: string;
        max_context_window: number;
        label: string;
        capabilities?: string[];
        provider: string;
        display_group?: string;
        order: number;
        thinking_options?: { type: "boolean" | "string"; options?: string[]; default?: string | boolean; disabled?: string[] };
        image_params?: ImageParam[];
      }[];
    }>(endpoint, {
      timeout: 5000,
    });
    models = [
      ...models,
      ...response.data.models.map(({ name, label, provider, max_context_window, capabilities, order, thinking_options, image_params, display_group }) => ({
        value: `default_${name}`,
        label,
        provider,
        max_context_window,
        serverId: "default",
        display_group,
        capabilities: capabilities != null ? capabilities : [],
        order: order,
        thinking_options: thinking_options,
        image_params: image_params,
      })),
    ];
  } catch (e: any) {
    console.log("Unable to retrieve models for ", endpoint);
    console.log(e);
  }
  models.sort((a, b) => {
    if (b.order !== a.order) {
      return b.order - a.order;
    }
    return a.label.localeCompare(b.label);
  });

  // Segment the sorted list into display groups: groups appear in order of their
  // first model, models keep the API-dictated order inside their group. Headers
  // come from i18n (`models.groups.{display_group}`); unknown groups fall back to
  // the raw key, models without one land in the fallback group ("other").
  const i18n = getI18n().global;
  const items: {
    label: string;
    options: LLMModel[];
  }[] = [];
  for (const model of models) {
    const key = model.display_group?.trim() || "other";
    const labelKey = `models.groups.${key}`;
    const label = i18n.te(labelKey) ? i18n.t(labelKey) : key;
    const existing = items.find((p) => p.label === label);
    if (existing) {
      existing.options.push(model);
    } else {
      items.push({
        label,
        options: [model],
      });
    }
  }

  return Promise.resolve(items);
};

export const getDocumentation = async (): Promise<{ name: string; label: string; content: string }[]> => {
  try {
    const result = await axios.get<{ name: string; label: string; content: string }[]>(`${AIStandardEndpoint}/documentation`);
    return Promise.resolve(result.data);
  } catch (error) {
    console.warn(error);
    return Promise.reject(null);
  }
};

export const updatePrompt = (prompt: LLMPrompt): Promise<LLMPrompt> => {
  return new Promise((resolve, reject) => {
    axios
      .put<LLMPrompt>(`${ServerName}/llm/prompt`, prompt)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        console.error("Unable to update prompt with error", error);
        return reject("Unable to update prompt");
      });
  });
};

export const getPrompts = async (): Promise<LLMPrompt[]> => {
  try {
    const response = await axios.get<LLMPrompt[]>(`${ServerName}/llm/prompt`);
    return Promise.resolve([...defaultPrompts, ...response.data]);
  } catch {
    return Promise.resolve([]);
  }
};

export const addPrompt = async (prompt: AddPromptDTO): Promise<LLMPrompt[]> => {
  try {
    const response = await axios.post(`${ServerName}/llm/prompt`, prompt);
    return Promise.resolve(response.data);
  } catch {
    return Promise.resolve([]);
  }
};

export const deletePrompts = async (promptIds: string[]): Promise<LLMPrompt[]> => {
  try {
    const response = await axios.delete(`${ServerName}/llm/prompt`, {
      data: promptIds,
    });
    return Promise.resolve(response.data);
  } catch {
    return Promise.resolve([]);
  }
};

export const defaultPrompts: LLMPrompt[] = [
  {
    id: "",
    name: "Vertragsparteien identifizieren",
    prompt: `
Instruction: Schreiben Sie den Firmennamen oder Personennamen und die Adressen der Vertragsparteien aus dem obigen Text wie folgt auf: 
Vertragspartei 1: Firmenname oder Personenname, Straße inkl. Hausnummer, Postleitzahl und Ort
Vertragspartei 2: Firmenname oder Personenname, Straße inkl. Hausnummer, Postleitzahl und Ort`,
  },
  {
    id: "",
    name: "Beantworten Sie zum obigen Vertrag die folgenden Fragen",
    prompt: `
Beantworten Sie zum obigen Vertrag die folgenden Fragen: 
1) Wie lange ist die Vertragslaufzeit?
2) Welche Kündigungsfrist gilt?
3) Gibt es eine Pönale bzw. Vertragsstrafe?
4) Welches Recht ist anwendbar?
5) Welcher Gerichtsstand wurde vereinbart?`,
  },
];

export type LLMStreamEventType =
  | "user_chat"
  | "assistant_chat"
  | "user_document"
  | "assistant_document"
  | "user_playbook"
  | "assistant_playbook"
  | "user_followup"
  | "assistant_followup"
  | "assistant_thoughts"
  | "sources"
  | "assistant_status"
  | "user_summary"
  | "assistant_summary"
  | "assistant_document"
  | "case_created"
  | "assistant_file"
  | "index_created"
  | "heartbeat"
  | "progress";

export const LLMStreamEventLabel: { [key in LLMStreamEventType]: string } = {
  user_chat: "Q",
  assistant_chat: "A",
  user_document: "Q",
  assistant_document: "A",
  user_playbook: "Abgleich mit Playbook",
  assistant_playbook: "Abgleich mit Playbook",
  user_followup: "Follow-up Prompt",
  assistant_followup: " Follow-up Antwort",
  sources: "",
  assistant_thoughts: "",
  assistant_status: "Status",
  user_summary: "Zusammenfassung",
  assistant_summary: "Kurze Zusammenfassung",
  heartbeat: "",
  case_created: "",
  assistant_file: "",
  index_created: "",
  progress: "",
};

export const LLMMessageTypeLabels: { [key: string]: string } = {
  chat: "Chat",
  document: "Chat mit Dokument",
  playbook: "Playbook Abgleich",
  followup: "Followup",
  status: "Status",
  summary: "Zusammenfassung",
};
export const getLLMMessageTypeLabel = (messageType: string) => {
  const matchingLabel = LLMMessageTypeLabels[messageType];
  if (matchingLabel) {
    return matchingLabel;
  }
  return "N/A";
};

export type LLMStreamType = "chat/completions" | "rag" | "workflow" | "workflow/remote" | "workflow/legacy" | "rag/summary";

export const deleteLLMCaseComplete = async (caseId: string) => {
  const endpoint = `${AIStandardEndpoint}/v1/case/${caseId}`;
  try {
    const response = await axios.delete(endpoint);
    return Promise.resolve();
  } catch (error) {
    console.warn(error);
    console.warn("Could not delete llm case index");

    if (axios.isAxiosError(error)) {
      console.warn("Error", error.response?.statusText || "Internal error");
    }
    return Promise.reject();
  }
};

export const createChatWithDocument = async (server: string, files: { file: File; filename: string }[], chunkSize?: number, chunkOverlap?: number): Promise<{ case_id: string; index_id: string }> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/chat/document`;
  const formData = new FormData();
  files.forEach(({ file, filename }) => {
    formData.append("file", file, filename);
  });

  if (chunkSize) {
    formData.append("chunk_size", chunkSize.toString());
  }
  if (chunkOverlap) {
    formData.append("chunk_overlap", chunkOverlap.toString());
  }
  const response = await axios.post<{ case_id: string; index_id: string }>(endpoint, formData);

  return Promise.resolve(response.data);
};

/** One page of the shared async job contract. See the API documentation's
 * "Status Updates" section: `progress.code` is a dotted status code, never
 * display text, `percent` is 0-100, and `detail` is optional phase metadata
 * (step positions, page counts) the UI may render when present. */
export type AsyncJobStepDetail = {
  step_index?: number;
  step_count?: number;
  // Additive page data. These are the CURRENT document's counters within its
  // current phase — not a sum across the job — and they restart at 1 for each
  // document, and again when a document enters its second page-bearing phase.
  // Overall progress therefore comes from `percent` alone. Deliberately not
  // rendered in the UI: OCR shows as a percent bar.
  pages_done?: number;
  pages_total?: number;
  document_index?: number;
  document_count?: number;
};

export type AsyncJobProgress = {
  code: string;
  percent: number;
  source: string;
  detail?: AsyncJobStepDetail;
};

export type AsyncJob<TResult = unknown> = {
  job_id: string;
  status: "queued" | "running" | "cancelling" | "completed" | "failed" | "cancelled";
  progress: AsyncJobProgress;
  queue_position: number | null;
  result: TResult | null;
  error: { code: string; message: string } | null;
  created_at: number;
  updated_at: number;
  expires_at: number;
};

const TERMINAL_JOB_STATUSES = ["completed", "failed", "cancelled"] as const;

export const isTerminalJobStatus = (status: AsyncJob["status"]): boolean => (TERMINAL_JOB_STATUSES as readonly string[]).includes(status);

/** Submit a chat-with-document job. Returns immediately with a job to poll
 * rather than waiting for extraction, which for audio can take many minutes. */
export const createChatWithDocumentAsync = async (
  server: string,
  files: { file: File; filename: string }[],
  chunkSize?: number,
  chunkOverlap?: number
): Promise<AsyncJob<{ case_id: string; index_id: string; text: string }>> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/async/chat/document`;
  const formData = new FormData();
  files.forEach(({ file, filename }) => {
    formData.append("file", file, filename);
  });

  if (chunkSize) {
    formData.append("chunk_size", chunkSize.toString());
  }
  if (chunkOverlap) {
    formData.append("chunk_overlap", chunkOverlap.toString());
  }

  const response = await axios.post<AsyncJob<{ case_id: string; index_id: string; text: string }>>(endpoint, formData);
  return Promise.resolve(response.data);
};

/** Fetch a job's current state. Always returns 200 while the job exists; a 404
 * means it is unknown, expired, or not ours — job state does not survive a
 * service restart, so a 404 on a job just submitted means resubmit. */
export const getAsyncJob = async <TResult = unknown>(server: string, jobId: string): Promise<AsyncJob<TResult>> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/async/job/${jobId}`;
  const response = await axios.get<AsyncJob<TResult>>(endpoint);
  return Promise.resolve(response.data);
};

export const cancelAsyncJob = async (server: string, jobId: string): Promise<void> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/async/job/${jobId}`;
  await axios.delete(endpoint);
  return Promise.resolve();
};

/** Submit a job and poll it to completion, with cooperative cancellation.
 *
 * `submit` picks the endpoint (chat-with-document, case documents, playbook
 * index); the runner owns everything after that: the poll loop, progress
 * reporting, and the DELETE /v2/async/job/:job_id call behind `cancel`.
 *
 * `cancel` is idempotent and safe to call at any point, including while the
 * submit is still in flight — the job does not exist yet, so the cancellation
 * is applied the moment the submit resolves. Cancelling after the job reached
 * a terminal state is a no-op on the backend, and the poll already holding a
 * terminal job simply wins the race.
 *
 * Resolves with the finished job whatever its outcome — including "cancelled":
 * the caller distinguishes outcomes via `job.status`, and a user-initiated
 * cancel should usually end quietly rather than as an error. */
export function runAsyncJob<TResult = unknown>(
  server: string,
  submit: () => Promise<AsyncJob<TResult>>,
  onProgress?: (progress: AsyncJobProgress) => void,
  options?: { intervalMs?: number }
): { promise: Promise<AsyncJob<TResult>>; cancel: () => Promise<void> } {
  let jobId: string | null = null;
  let cancelRequested = false;

  const promise = (async () => {
    const submitted = await submit();
    jobId = submitted.job_id;
    if (cancelRequested) {
      try {
        await cancelAsyncJob(server, jobId);
      } catch {
        // Terminal or already gone — nothing left to cancel either way.
      }
    }
    return pollAsyncJob<TResult>(server, jobId, onProgress, { intervalMs: options?.intervalMs });
  })();

  const cancel = async (): Promise<void> => {
    cancelRequested = true;
    if (jobId == null) return;
    try {
      await cancelAsyncJob(server, jobId);
    } catch {
      // Terminal or expired meanwhile; the poll result reports the outcome.
    }
  };

  return { promise, cancel };
}

/** A 404 on a poll means the job is unknown, expired, or not ours — job state
 * does not survive a service restart. Resolved as a synthetic failed job so
 * callers can treat it exactly like a server-reported failure: the code is
 * mapped through the same llm.progress.* locale entries. */
export const JOB_LOST_ERROR_CODE = "orchestration.job_lost";

const syntheticJobLost = <TResult = unknown>(jobId: string): AsyncJob<TResult> => {
  const now = Date.now();
  return {
    job_id: jobId,
    status: "failed",
    progress: { code: JOB_LOST_ERROR_CODE, percent: 0, source: "llm-orchestration" },
    queue_position: null,
    result: null,
    error: { code: JOB_LOST_ERROR_CODE, message: "Job record is unknown, expired, or not accessible" },
    created_at: now,
    updated_at: now,
    expires_at: now,
  };
};

/** Whether a poll failure means the job record is gone for good.
 *
 * Two wire shapes, depending on the endpoint's domain:
 * - a plain AxiosError with status 404 on domains that pass responses through,
 * - the interceptor's rewrite for domains in errorCodeDomains, which replaces
 *   the AxiosError with {message, code, detail} and drops the status. The
 *   orchestrator's job-not-found 404 carries no error code, so it arrives as
 *   errors.backend.unknown_error. Once the orchestrator adds a dedicated code
 *   (job.not_found) to those 404s, add the rewritten form here and the mapping
 *   becomes exact rather than by absence of a code.
 *
 * One failing poll is not enough to declare the job lost — a single blip must
 * not kill a twenty-minute transcription. The caller confirms the shape twice. */
const isJobGoneError = (e: unknown): boolean => {
  if (axios.isAxiosError(e)) {
    return e.response?.status === 404;
  }
  return (e as { code?: string } | null)?.code === "errors.backend.unknown_error";
};

const isTransientPollError = (e: unknown): boolean => {
  if (!axios.isAxiosError(e)) return false;
  // No response = network failure; 5xx = the server blinked. Both can clear on
  // the next poll. Everything else (4xx, auth failures) is permanent.
  return e.response == null || e.response.status >= 500;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Poll a job to completion, reporting progress as it arrives.
 *
 * `onProgress` receives the raw status code and percent — the caller maps the
 * code to display text, so unknown codes can fall back gracefully rather than
 * being shown raw. Resolves with the finished job whatever its outcome; the
 * caller inspects `status` and `error`.
 *
 * Two failure modes are handled in-band rather than thrown:
 * - a confirmed job-gone poll (404, see isJobGoneError) resolves a synthetic
 *   failed job carrying orchestration.job_lost, and
 * - transient network/server failures are retried up to three consecutive
 *   times before giving up, so one blip does not kill a long extraction. */
export const pollAsyncJob = async <TResult = unknown>(
  server: string,
  jobId: string,
  onProgress?: (progress: AsyncJobProgress) => void,
  options?: { intervalMs?: number; signal?: AbortSignal }
): Promise<AsyncJob<TResult>> => {
  const intervalMs = options?.intervalMs ?? 2000;
  let goneFailures = 0;
  let transientFailures = 0;

  for (;;) {
    if (options?.signal?.aborted) {
      throw new DOMException("Job polling aborted", "AbortError");
    }

    let job: AsyncJob<TResult>;
    try {
      job = await getAsyncJob<TResult>(server, jobId);
      goneFailures = 0;
      transientFailures = 0;
    } catch (e) {
      if (isJobGoneError(e)) {
        if (++goneFailures >= 2) return syntheticJobLost<TResult>(jobId);
      } else if (isTransientPollError(e)) {
        // A dropped poll must not kill a long extraction.
        if (++transientFailures >= 3) throw e;
      } else {
        throw e;
      }
      await sleep(intervalMs);
      continue;
    }

    if (job.progress?.code) {
      onProgress?.(job.progress);
    }
    if (isTerminalJobStatus(job.status)) {
      return job;
    }

    await sleep(intervalMs);
  }
};

/** Submit an add-documents-to-case job. Returns immediately with a job to poll
 * rather than waiting for extraction, which for audio can take many minutes. */
export const addDocumentsToLLMCaseAsync = async (server: string, caseId: string, files: { file: File; filename: string }[], chunkSize?: number, chunkOverlap?: number): Promise<AsyncJob<unknown>> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/async/case/${caseId}/document`;
  const formData = new FormData();
  // Note the field name: the async endpoint takes "file", matching every other
  // upload in the v2 API. The synchronous /v1 route below uses "files".
  files.forEach(({ file, filename }) => {
    formData.append("file", file, filename);
  });

  if (chunkSize) {
    formData.append("chunk_size", chunkSize.toString());
  }
  if (chunkOverlap) {
    formData.append("chunk_overlap", chunkOverlap.toString());
  }

  const response = await axios.post<AsyncJob<unknown>>(endpoint, formData);
  return Promise.resolve(response.data);
};

export const addDocumentsToLLMCase = async (
  server: string,
  caseId: string,
  files: { file: File; filename: string }[],
  chunkSize?: number,
  chunkOverlap?: number
): Promise<{ case_id: string; index_id: string }> => {
  const endpoint = `${getLLMEndpoint(server)}/v1/case/${caseId}/document`;
  const formData = new FormData();
  files.forEach(({ file, filename }) => {
    formData.append("files", file, filename);
  });

  if (chunkSize) {
    formData.append("chunk_size", chunkSize.toString());
  }
  if (chunkOverlap) {
    formData.append("chunk_overlap", chunkOverlap.toString());
  }
  const response = await axios.post<{ case_id: string; index_id: string }>(endpoint, formData);

  return Promise.resolve(response.data);
};

export const createChatWithPlaybook = async (server: string, playbookId: string): Promise<{ case_id: string }> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/chat/playbook/${playbookId}`;
  const response = await axios.post<{ case_id: string }>(endpoint);
  return Promise.resolve(response.data);
};

export const createChat = async (server: string): Promise<{ case_id: string }> => {
  const endpoint = `${getLLMEndpoint(server)}/v2/chat`;
  const response = await axios.post<{ case_id: string }>(endpoint);
  return Promise.resolve(response.data);
};

interface exportLLMMessagesParams {
  caseId: string;
  type?: string[];
  topic?: string[];
  prompt?: string[];
  uuid?: string[];
  filename?: string;
  fontFamily: string;
  fileFormat: string;
}

export const exportLLMMessages = async (params: exportLLMMessagesParams) => {
  try {
    const { caseId, filename, type, topic, prompt, fontFamily, fileFormat, uuid } = params;
    const response = await axios.get(`${ServerName}/llm/export/${caseId}`, {
      responseType: "blob",
      params: {
        type: type,
        topic: topic,
        prompt: prompt,
        font_family: fontFamily,
        uuid: uuid,
        file_format: fileFormat,
      },
      paramsSerializer: (params) => {
        return Object.keys(params)
          .filter((p) => typeof params[p] !== "undefined")
          .map((key) => {
            if (Array.isArray(params[key])) {
              return params[key].map((value) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join("&");
            } else {
              return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
            }
          })
          .join("&");
      },
    });
    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${filename ? filename : "export"}.${fileFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return Promise.resolve();
  } catch (e: any) {
    console.error("Unable to get exported messages");
    return Promise.reject();
  }
};

export const exportSearchablePDFs = async (caseId: string) => {
  try {
    const response = await axios.get(`${ServerName}/llm/export/documents/${caseId}`, {
      responseType: "blob",
      headers: { Accept: "*/*" },
    });

    const contentDisposition = response.headers["content-disposition"];

    let filename = "export.pdf";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=UTF-8''([^"]+)/);
      if (filenameMatch && filenameMatch.length > 1) {
        try {
          filename = decodeURIComponent(filenameMatch[1]);
        } catch (error) {
          filename = filenameMatch[1];
        }
      }
    }
    const blob = new Blob([response.data]);

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${filename ? filename : filename}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return Promise.resolve(blob);
  } catch (error: any | AxiosError) {
    if (axios.isAxiosError(error)) {
      if (error.status == 404) {
        showNotification("error", "Es wurden keine durchsuchbare PDFs gefunden");
      } else {
        showNotification("error", "Export fehlgeschlagen");
      }
      return Promise.resolve();
    } else {
      showNotification("error", "Export fehlgeschlagen");
    }
  }
};
